/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import remove from 'lodash.remove';
import { action, extendObservable, observable } from 'mobx';
import { createEntityStore } from 'src/luis/core/stores/entityStore';
import { createFeatureStoreHouse } from 'src/luis/core/stores/featureStoreHouse';
import { ModelType } from 'src/luis/types/ModelType';
import { LuisId } from 'src/luis/types/Resource';
import { EntityStore } from 'src/luis/types/stores/entities/EntityStore';
import { SimpleEntityStore } from 'src/luis/types/stores/entities/SimpleEntityStore';
import { SubtractStore } from 'src/luis/types/SubtractStore';

/**
 * A simple entity store factory function.
 *
 * @param id The id of the entity to create.
 * @param entity The simple entity data.
 * @returns A new instance of a simple entity store.
 */
export const createSimpleEntityStore = (id: LuisId, entity: Partial<SimpleEntityStore>): SimpleEntityStore => {
    const entityStore = createEntityStore(id, { ...entity, typeId: ModelType.Simple });
    const simpleEntityStore: SubtractStore<SimpleEntityStore, EntityStore> = {
        updateCount: 0,
        children: entity.children || [],
        featureStoreHouse: createFeatureStoreHouse(),
        instanceOf: entity.instanceOf,

        incrementUpdateCount() {
            this.updateCount++;
        },

        /**
         * Adds the given entity to the array of children
         * of the entity and returns the new array length.
         *
         * @param child The child to add.
         * @returns The new length of the children array after the
         * child was added.
         */
        addChild(childId: LuisId, childEntity: Partial<SimpleEntityStore>) {
            const child = createSimpleEntityStore(childId, childEntity);

            return this.children.push(child);
        },

        /**
         * Removes the child that has the given id from the
         * array of children of the entity.
         *
         * @param childId The id of the child to delete.
         * @returns True if deletion was successful and
         * false otherwise.
         */
        removeChildById(childIdToRemove) {
            if (remove(this.children, child => child.id === childIdToRemove).length > 0) {
                return true;
            }
            for (const child of this.children) {
                const deleted = (<SimpleEntityStore>child).removeChildById(childIdToRemove);
                if (deleted) {
                    return true;
                }
            }

            return false;
        },

        /**
         * Retrieves a simple child entity by certain predicate from
         * the array of children of the entity.
         *
         * @param predicate The predicate to find the child with.
         * @returns The child simple entity if found, undefined otherwise.
         */
        getChild(predicate: (child: SimpleEntityStore) => boolean) {
            const foundEntity = this.children.find(predicate);
            if (foundEntity) {
                return foundEntity;
            }

            for (const child of this.children) {
                const foundChild = (<SimpleEntityStore>child).getChild(predicate);
                if (foundChild) {
                    return foundChild;
                }
            }

            return undefined;
        },

        /**
         * Retrieves a simple child entity by its id from
         * the array of children of the entity.
         *
         * @param childIdToFind The id of the child to find.
         * @returns The child simple entity if found, undefined otherwise.
         */
        getChildById(childIdToFind) {
            return this.getChild(({ id: childId }) => childId === childIdToFind);
        },

        /**
         * Retrieves a simple child entity by its name from
         * the array of children of the entity.
         *
         * @param childNameToFind The name of the child to find.
         * @returns The child simple entity if found, undefined otherwise.
         */
        getChildByName(childNameToFind) {
            return this.getChild(({ name: childName }) => childName === childNameToFind);
        },

        /**
         * sets the children array.
         *
         * @param children The new children array.
         */
        setChildren(children) {
            this.children = children;
        },

        /**
         * Updates the instanceOf field (the non ML entity set as a dependency).
         *
         * @param newInstanceOf the id of the non ML entity to be set as a dependency.
         */
        updateInstanceOf(newInstanceOf) {
            this.instanceOf = newInstanceOf;
        },

        /**
         * Deletes the instanceOf field (the non ML entity set as a dependency).
         */
        deleteInstanceOf() {
            this.instanceOf = undefined;
        }
    };

    return extendObservable(
        entityStore,
        simpleEntityStore,
        {
            children: observable,
            updateCount: observable,
            instanceOf: observable,
            addChild: action,
            removeChildById: action,
            getChild: action,
            getChildByName: action,
            getChildById: action,
            setChildren: action,
            updateInstanceOf: action,
            deleteInstanceOf: action,
            incrementUpdateCount: action
        },
        { deep: false }
    );
};
